import { useState } from 'react';
import { NavLink, Outlet, useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  LayoutDashboard, MessageSquare, Users, Radio,
  Plug, Bot, CreditCard, UserCog, Settings,
  LogOut, Wifi, WifiOff, ClipboardList, User, Sun, Moon, Briefcase,
  FileSpreadsheet,
} from 'lucide-react';

const allNavItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Overview', end: true, perm: null },
  { to: '/dashboard/inbox', icon: MessageSquare, label: 'Inbox', perm: 'inbox' },
  { to: '/dashboard/crm', icon: Briefcase, label: 'CRM', perm: 'crm' },
  { to: '/dashboard/bookings', icon: ClipboardList, label: 'Bookings', perm: 'bookings' },
  { to: '/dashboard/customers', icon: Users, label: 'Customers', perm: 'customers' },
  { to: '/dashboard/channels', icon: Radio, label: 'Channels', perm: 'channels' },
  { to: '/dashboard/integrations', icon: Plug, label: 'Integrations', perm: 'integrations' },
  { to: '/dashboard/ai-settings', icon: Bot, label: 'AI Settings', perm: 'ai_settings' },
  { to: '/dashboard/extraction', icon: FileSpreadsheet, label: 'Extraction', perm: 'extraction' },
];

const allBottomNav = [
  // { to: '/dashboard/billing', icon: CreditCard, label: 'Billing', perm: 'billing' },
  { to: '/dashboard/agents', icon: UserCog, label: 'Team', perm: 'agents' },
  { to: '/dashboard/profile', icon: Settings, label: 'Settings', perm: 'settings' },
];

function NavItem({ to, icon: Icon, label, end }) {
  return (
    <NavLink to={to} end={end}
      className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
      <Icon size={17} />
      <span>{label}</span>
    </NavLink>
  );
}

export default function DashboardLayout() {
  const { user, merchant, logout } = useAuth();
  const { connected } = useSocket();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const toggleSidebar = () => setSidebarHidden(p => !p);

  // Agents with permissions only see allowed sections. Merchants see all. Empty permissions = all access.
  const perms = user?.permissions || [];
  const hasFullAccess = user?.role === 'merchant' || perms.length === 0;
  const canSee = (perm) => !perm || hasFullAccess || perms.includes(perm);
  // Feature flags gated by super admin (merchant.features)
  const featureOk = (to) => {
    if (to === '/dashboard/extraction') return merchant?.features?.extractionEnabled === true;
    return true;
  };
  const navItems = allNavItems.filter(i => canSee(i.perm) && featureOk(i.to));
  const bottomNav = allBottomNav.filter(i => canSee(i.perm) && featureOk(i.to));

  const initials = (name) => name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '??';

  return (
    <div className="app-layout">
      <aside className="sidebar" style={{
        width: sidebarHidden ? 0 : undefined,
        minWidth: sidebarHidden ? 0 : undefined,
        overflow: sidebarHidden ? 'hidden' : undefined,
        padding: sidebarHidden ? 0 : undefined,
        border: sidebarHidden ? 'none' : undefined,
        transition: 'all 0.2s ease',
      }}>
        {/* Logo */}
        <div className="sidebar-logo">
          <img
            src={theme === 'dark' ? '/ainasuite_dark_top.png' : '/logo.png'}
            alt="AinaSuite"
            className="sidebar-logo-desktop"
          />
          <img src="/logos.png" alt="AinaSuite" className="sidebar-logo-mobile" />
        </div>

        {/* Merchant name */}
        {merchant && (
          <div style={{ padding: '10px 16px 6px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Workspace</div>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {merchant.businessName}
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="sidebar-nav">
          <div className="sidebar-section">Main</div>
          {navItems.map(item => <NavItem key={item.to} {...item} />)}
          <div className="sidebar-section" style={{ marginTop: 8 }}>Account</div>
          {bottomNav.map(item => <NavItem key={item.to} {...item} />)}
          {user?.role === 'agent' && <NavItem to="/dashboard/my-account" icon={User} label="My Account" />}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          {/* Theme toggle */}
          <button onClick={toggleTheme} className="nav-item" style={{ marginBottom: 4, opacity: 1 }}>
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
            <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
          {/* Connection indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', marginBottom: 6 }}>
            {connected
              ? <><Wifi size={12} color="var(--green)" /><span style={{ fontSize: '0.72rem', color: 'var(--green)' }}>Live</span></>
              : <><WifiOff size={12} color="var(--text-muted)" /><span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Offline</span></>
            }
          </div>
          <div className="sidebar-user" onClick={handleLogout} title="Sign out">
            <div className="avatar avatar-sm">{initials(user?.name)}</div>
            <div className="user-info" style={{ flex: 1, overflow: 'hidden' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{user?.role}</div>
            </div>
            <LogOut size={14} color="var(--text-muted)" />
          </div>
        </div>
      </aside>

      <main className="main-content">
        <Outlet context={{ sidebarHidden, toggleSidebar }} />
      </main>
    </div>
  );
}
